import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";

import { FormHelper, StyleHelper } from "utils";
import { Perfil } from "models/Perfil";
import { GridView } from "components";
import { Simplebar } from "components/Simplebar/Simplebar";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import RadioGroup from "components/RadioGroup/RadioGroup";
import ToolTip from "components/ToolTip/ToolTip";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { PerfilManager, FuncionManager } from "managers";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import { 
  CATEGORIAS, 
  PERMISOS_CONFIG,
  COLUMNA_LISTAR,
  COLUMNA_EDITAR,
  COLUMNA_CREAR,
  COLUMNA_CANCELAR,
  COLUMNA_OTROS,
  COLUMNA_IMPRIMIR
} 
from "utils/permisosConfig";
import PermisosOptions from "./PermisosOptions";

const PERMISO_INDEX = new Map(PERMISOS_CONFIG.map((p) => [p.nombre, p]));

const CATCHALL_CATEGORIA = CATEGORIAS.includes("Otros")
  ? "Otros (Sin Categoría)"
  : "Otros";

const accionSegment = (nombre) => {
  if (!nombre) return "";
  const parts = nombre.split(".");
  return parts[parts.length - 1] || nombre;
};

export default function PerfilEdit({
  showLoading,
  hideLoading,
  IdSec,
  esClonar = false,
  showCanvas,
  setShowCanvas,
  updateList = () => { },
}) {
  const [perfil, setPerfil] = useState({});
  const [esNuevo, setEsNuevo] = useState(true);
  const [tituloHeader, setTituloHeader] = useState("Crear Perfil");
  const [funcionesPorAgregarList, setFuncionesPorAgregarList] = useState([]);
  const [funcionesPorAgregarGrid, setFuncionesPorAgregarGrid] = useState([]);
  const [entidadesList, setEntidadesList] = useState([]);
  const [todosCheck, setTodosCheck] = useState([]);
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState({ EsPaginable: false });
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();
  const ref = useRef(null);
  const ESTATUS_LIST = Perfil.ESTATUS_LIST;

  useEffect(() => {
    if (!showCanvas) return;
    setEsNuevo(!IdSec || esClonar);
    if (IdSec) {
      showLoading();
      cargarPerfil(IdSec);
      esClonar
        ? setTituloHeader("Clonar Perfil")
        : setTituloHeader("Editar Perfil");
    } else {
      setTituloHeader("Crear Perfil");
      setDefaultValues();
      setFuncionesPorAgregarList([]);
      setFuncionesPorAgregarGrid([]);
      setEntidadesList([]);
      setTodosCheck([]);
    }
    return () => {
      hideLoading();
      cerrarAlertas();
    };
  }, [IdSec, showCanvas]);

  useEffect(() => {
    if (Object.keys(where).length > 0) {
      setFuncionesPorAgregarGrid(filterList());
    } else {
      setFuncionesPorAgregarGrid([...funcionesPorAgregarList]);
    }
  }, [where, funcionesPorAgregarList]);

  useEffect(() => {
    setEntidadesList(buildCategoriaRows(funcionesPorAgregarGrid));
  }, [funcionesPorAgregarGrid]);

  useEffect(() => {
    setTodosCheck((prev) => {
      const next = new Set(prev);
      entidadesList.forEach((row) => {
        const funciones = rowFunciones(row);
        if (funciones.length === 0) {
          next.delete(row.Key);
          return;
        }
        const allOn = funciones.every((f) => f.EsAnadirPerfil === true);
        if (allOn) next.add(row.Key);
        else next.delete(row.Key);
      });
      return Array.from(next);
    });
  }, [entidadesList]);

  useEffect(() => {
    if (entidadesList.length === 0) return;
    const fmt = (f) =>
      f ? `${f.IdSec}${f.EsAnadirPerfil ? " ✓" : ""}` : "—";
    const fmtList = (items) =>
      items.length === 0
        ? "—"
        : items
            .map(({ funcion, label }) =>
              `${label}${funcion.EsAnadirPerfil ? " ✓" : ""}`
            )
            .join(", ");
    const debug = entidadesList.map((row) => ({
      Categoria: row.Categoria,
      Todos: todosCheck.includes(row.Key),
      Listar: fmt(row.Listar),
      Crear: fmt(row.Crear),
      Editar: fmt(row.Editar),
      Cancelar: fmt(row.Cancelar),
      Imprimir: fmtList(row.Imprimir),
      Otros: fmtList(row.Otros),
    }));
    console.group(
      `[PerfilEdit] Permisos grid — ${debug.length} categorias, ${funcionesPorAgregarGrid.length} funciones`
    );
    console.table(debug);
    console.groupEnd();
  }, [entidadesList, funcionesPorAgregarGrid, todosCheck]);

  const setDefaultValues = () => {
    setPerfil({
      Estatus: Perfil.DEFAULT_ESTATUS_VALUE,
      LlamadaCondicion: Perfil.CONDICION_LLAMADA_EN_PROCESO,
    });
  };

  const cargarPerfil = (Id) => {
    PerfilManager.buscarPorId(Id)
      .then((data) => {
        setPerfil(data);
        cargarFuncionesList(data.IdSec, data.Funciones);
        setWhere({});
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  };

  const grabar = () => {
    showLoading();
    if (esNuevo) {
      PerfilManager.crear(perfil)
        .then((res) => {
          setEsNuevo(false);
          Message({ message: `Grabado Exitosamente` });
          cargarPerfil(res.IdSec);
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    } else {
      PerfilManager.actualizar(perfil)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarPerfil(perfil.IdSec);
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    }
  };

  function cargarFuncionesList(Id, funcionesList) {
    FuncionManager.buscarNoEnPerfilId(Id)
      .then((res) => {
        if (res && res.Headers) {
          setPaginationValuesWhere(res.Headers, pagination, setPagination);
        }
        const combined = res.concat(funcionesList);

        const byNombre = new Map();
        combined.forEach((f) => {
          const existing = byNombre.get(f.Nombre);
          if (!existing || f.IdSec < existing.IdSec) byNombre.set(f.Nombre, f);
        });
        const list = Array.from(byNombre.values());

        const assignedIds = new Set((funcionesList ?? []).map((f) => f.IdSec));
        const assignedNombres = new Set(
          (funcionesList ?? []).map((f) => f.Nombre)
        );
        list.forEach((f) => {
          f.EsAnadirPerfil =
            assignedIds.has(f.IdSec) || assignedNombres.has(f.Nombre);
        });

        sortPermisosList(list);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  }

  const filterList = () => {
    return [...funcionesPorAgregarList].filter((el) => {
      return (
        (where.IdSec
          ? el.IdSec?.toString().includes(where.IdSec?.toString())
          : true) &&
        (where.Nombre
          ? el.Nombre?.toLowerCase().includes(where.Nombre?.toLowerCase())
          : true) &&
        (where.Descripcion
          ? el.Descripcion?.toLowerCase().includes(
            where.Descripcion?.toLowerCase()
          )
          : true)
      );
    });
  };

  function sortPermisosList(list) {
    if (!list) {
      list = [...funcionesPorAgregarList];
    }
    list.sort((a, b) => Number(b.EsAnadirPerfil) - Number(a.EsAnadirPerfil));
    setFuncionesPorAgregarList(list);
    setFuncionesPorAgregarGrid(list);
  }

  function buildCategoriaRows(funciones) {
    const empty = () => ({
      Listar: null,
      Crear: null,
      Editar: null,
      Cancelar: null,
      Imprimir: [],
      Otros: [],
    });
    const buckets = new Map();
    CATEGORIAS.forEach((cat, idx) =>
      buckets.set(cat, { Key: idx + 1, Categoria: cat, ...empty() })
    );

    funciones.forEach((f) => {
      const cfg = PERMISO_INDEX.get(f.Nombre);
      const categoria = cfg ? cfg.categoria : CATCHALL_CATEGORIA;
      const columna = cfg ? cfg.columna : "OTROS";
      const label = cfg ? cfg.label : accionSegment(f.Nombre);

      let row = buckets.get(categoria);
      if (!row) {
        row = { Key: buckets.size + 1, Categoria: categoria, ...empty() };
        buckets.set(categoria, row);
      }

      switch (columna) {
        case COLUMNA_LISTAR:
          row.Listar = f;
          break;
        case COLUMNA_CREAR:
          row.Crear = f;
          break;
        case COLUMNA_EDITAR:
          row.Editar = f;
          break;
        case COLUMNA_CANCELAR:
          row.Cancelar = f;
          break;
        case COLUMNA_IMPRIMIR:
          row.Imprimir.push({ funcion: f, label });
          break;
        case COLUMNA_OTROS:
        default:
          row.Otros.push({ funcion: f, label });
          break;
      }
    });

    const isEmpty = (row) =>
      !row.Listar &&
      !row.Crear &&
      !row.Editar &&
      !row.Cancelar &&
      row.Imprimir.length === 0 &&
      row.Otros.length === 0;

    const ordered = CATEGORIAS.map((c) => buckets.get(c)).filter((r) => r && !isEmpty(r));
    const catchAll = buckets.get(CATCHALL_CATEGORIA);
    if (catchAll && !isEmpty(catchAll) && !CATEGORIAS.includes(CATCHALL_CATEGORIA)) {
      ordered.push(catchAll);
    }
    return ordered;
  }

  function rowFunciones(row) {
    const out = [];
    if (row.Listar) out.push(row.Listar);
    if (row.Crear) out.push(row.Crear);
    if (row.Editar) out.push(row.Editar);
    if (row.Cancelar) out.push(row.Cancelar);
    row.Imprimir.forEach((it) => out.push(it.funcion));
    row.Otros.forEach((it) => out.push(it.funcion));
    return out;
  }

  function addToList(Id) {
    const funcion = funcionesPorAgregarList.find((f) => f.IdSec === Id);
    if (!funcion) return;
    const exists = perfil.Funciones?.some((f) => f.IdSec === Id);
    if (exists) return;
    setPerfil((prev) => ({
      ...prev,
      Funciones: [...(prev.Funciones ?? []), { ...funcion, EsAnadirPerfil: true }],
    }));
  }

  function removeFromList(Id) {
    setPerfil((prev) => ({
      ...prev,
      Funciones: (prev.Funciones ?? []).filter((f) => f.IdSec !== Id),
    }));
  }

  function handleFuncionSwitch(Id, e) {
    const index = funcionesPorAgregarList.findIndex((f) => f.IdSec === Id);
    if (index === -1) return;
    const checked = e?.target?.checked ?? !funcionesPorAgregarList[index].EsAnadirPerfil;
    if (checked) {
      addToList(Id);
    } else {
      removeFromList(Id);
    }
    const updater = (prev) =>
      prev.map((f) => (f.IdSec === Id ? { ...f, EsAnadirPerfil: checked } : f));
    setFuncionesPorAgregarList(updater);
    setFuncionesPorAgregarGrid(updater);
  }

  const checkAllByIdList = (idList) => {
    const tempGrid = funcionesPorAgregarGrid.map((el) => {
      if (idList.includes(el.IdSec)) {
        addToList(el.IdSec);
        return { ...el, EsAnadirPerfil: true };
      }
      return el;
    });
    const tempList = funcionesPorAgregarList.map((el) => {
      const match = tempGrid.find((f) => f.IdSec === el.IdSec);
      return match ? { ...el, EsAnadirPerfil: match.EsAnadirPerfil } : el;
    });
    setFuncionesPorAgregarGrid(tempGrid);
    setFuncionesPorAgregarList(tempList);
  };

  const unCheckAllByIdList = (idList) => {
    const tempGrid = funcionesPorAgregarGrid.map((el) => {
      if (idList.includes(el.IdSec)) {
        removeFromList(el.IdSec);
        return { ...el, EsAnadirPerfil: false };
      }
      return el;
    });
    const tempList = funcionesPorAgregarList.map((el) => {
      const match = tempGrid.find((f) => f.IdSec === el.IdSec);
      return match ? { ...el, EsAnadirPerfil: match.EsAnadirPerfil } : el;
    });
    setFuncionesPorAgregarGrid(tempGrid);
    setFuncionesPorAgregarList(tempList);
  };

  const renderActionCheck = (funcion, accion) => {
    if (!funcion) return null;
    const check = (
      <Form.Check
        name={accion}
        style={{ textAlign: "center" }}
        onChange={(e) => handleFuncionSwitch(funcion.IdSec, e)}
        checked={funcion.EsAnadirPerfil === true}
      />
    );
    return funcion.Descripcion ? (
      <ToolTip message={funcion.Descripcion}>{check}</ToolTip>
    ) : (
      check
    );
  };

  const renderColumnHeader = (label) => (
    <div className="d-flex justify-content-center text-black">
      <p style={{ fontWeight: "bold", margin: 0 }}>{label}</p>
    </div>
  );

  const FuncionesGridCheck = (
    <div>
      <GridView
        pagination={{}}
        setPagination={{}}
        allowPaging={false}
        pageSize={{}}
        pageIndex={{}}
        lastPageNumber={0}
        dataSource={entidadesList}
        where={{}}
        setWhere={() => {}}
        height={"calc(100vh - 320px)"}
      >
        <GridView.Column
          forceRender={true}
          filterRenderer={() => <div></div>}
          dataField="Categoria"
          width="22%"
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Todos")}
          bodyRenderer={(row) => {
            const funciones = rowFunciones(row);
            if (funciones.length === 0) return null;
            return (
              <Form.Check
                name="Todos"
                style={{ textAlign: "center" }}
                onChange={() => {
                  const ids = funciones.map((f) => f.IdSec);
                  if (todosCheck.includes(row.Key)) {
                    unCheckAllByIdList(ids);
                    setTodosCheck((prev) => prev.filter((t) => t !== row.Key));
                  } else {
                    checkAllByIdList(ids);
                    setTodosCheck((prev) => [...prev, row.Key]);
                  }
                }}
                checked={todosCheck.includes(row.Key)}
              />
            );
          }}
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Listar")}
          bodyRenderer={(row) => renderActionCheck(row.Listar, "LISTAR")}
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Crear")}
          bodyRenderer={(row) => renderActionCheck(row.Crear, "CREAR")}
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Editar")}
          bodyRenderer={(row) => renderActionCheck(row.Editar, "EDITAR")}
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Cancelar")}
          bodyRenderer={(row) => renderActionCheck(row.Cancelar, "CANCELAR")}
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Imprimir")}
          bodyRenderer={(row) =>
            row.Imprimir.length > 0 ? (
              <PermisosOptions
                items={row.Imprimir}
                handleFuncionSwitch={handleFuncionSwitch}
              />
            ) : null
          }
        />
        <GridView.Column
          forceRender={true}
          filterRenderer={() => renderColumnHeader("Otros")}
          bodyRenderer={(row) =>
            row.Otros.length > 0 ? (
              <PermisosOptions
                items={row.Otros}
                handleFuncionSwitch={handleFuncionSwitch}
              />
            ) : null
          }
        />
      </GridView>
    </div>
  );

  return (
    <Simplebar
      ref={ref}
      backdrop="static"
      confirmBack
      placement="end"
      size="xxl"
      portal={false}
      visible={showCanvas}
      setShow={(e) => setShowCanvas(e)}
      useLoading={true}
      title={tituloHeader}
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <Form action="" className="form-horizontal" method="">
        <EditContainer>
          <EditSection title="Información General" style={{}}>
            <SectionRow>
              <InputContainer stacked label="Nombre">
                <Form.Control
                  name="Nombre"
                  type="text"
                  onChange={(e) => FormHelper.handleChange(e, setPerfil)}
                  value={perfil.Nombre}
                  style={StyleHelper.InputRequired}
                />
              </InputContainer>

              <InputContainer stacked label="Descripción">
                <Form.Control
                  onChange={(e) => FormHelper.handleChange(e, setPerfil)}
                  name="Descripcion"
                  type="text"
                  value={perfil.Descripcion}
                  style={StyleHelper.InputRequired}
                />
              </InputContainer>
            </SectionRow>

            <SectionRow>
              <InputContainer stacked label="Estatus">
                <RadioGroup
                  name="Estatus"
                  disabled={esNuevo}
                  options={ESTATUS_LIST}
                  selectedOption={perfil.Estatus}
                  onChange={(e) => {
                    setPerfil({
                      ...perfil,
                      Estatus: e.target.value
                    })
                  }}
                />
              </InputContainer>
            </SectionRow>
          </EditSection>
          
          {!esNuevo && (
            <EditSection title="Permisos" style={{}}>{FuncionesGridCheck}</EditSection>
          )}
        </EditContainer>
      </Form>
    </Simplebar>
  );
}
